/**
 * Table.tsx —— 主牌桌（设计方案 4.3）
 *
 * 两种席位布局：
 *
 *   - `oval`（宽屏）：席位沿**环形轨道**分布，坐标由 `seatLayout.planRing` 按
 *     「实测容器盒 × 人数 × 卡片档位 × 障碍物（我的排他区 / 受保护中心带 / 赢率浮层）」算出，
 *     以 px 落在 inline style 上。真人恒在底部中央并独占排他区。
 *   - `stack`（窄屏）：三段纵向结构（公共牌带 / 对手网格 / 我），
 *     网格的列数与行高由 `seatLayout.planGrid` 按「不滚动就放得下」反解，
 *     不再依赖断点写死的 2 列 / 3 列。
 *
 * 两种布局共用同一套 DOM 与同一套档位阶梯（full → compact → row → micro），
 * 档位由算法给出，CSS 只负责按 `.felt--seats-*` 呈现，不再有任何「按断点微调半径」的补丁。
 *
 * 量测口径：`.felt` 的 padding box 即席位层坐标系原点（`.felt__seats` 是 inset:0 的绝对层，
 * 真人席位是 `.felt` 的直接子节点，两者共用同一个绝对定位包含块，因此坐标可直接互比）。
 */

import { useCallback, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import type { Game } from '../core/game';
import { describeHandValue, evaluateHoleAndBoard } from '../core/evaluator';
import { chips, chipsShort, pillInfo, STREET_CN } from '../state/format';
import type { GameController } from '../state/controller';
import { PlayingCard } from './primitives';
import { SeatCard } from './SeatCard';
import {
  planGrid,
  planRing,
  planClusters,
  clusterPlanToSnapshot,
  validateClusters,
  DENSITIES_PORTRAIT,
  type Rect,
  type RingPlan,
  type SeatDensity,
  type Size,
} from './seatLayout';
import { solveOverlayLayout, enforceAttribution, type OverlayInput, type SeatRuntime, type OBB } from './overlayLayout';

export type SeatLayout = 'oval' | 'stack';

/**
 * 阶段2（M2）· 席位簇模板特性开关（PRD §10 回退开关）。
 *
 * `true`：竖屏 overlay 走「簇模板构造」`planClusters → clusterPlanToSnapshot`（S2-1~S2-6，
 * 元素退化为按 cluster 模板纯映射）。构造后用 `validateClusters` 自校验，**任何异常或校验不过
 * 一律回退阶段1 求解器 `solveOverlayLayout`**（同一 `OverlayInput` → 同一 `LayoutSnapshot`
 * 结构 → 下游 chipDots/actPills/oppCards/blindBadges/dealer 派生逻辑零改动）。
 * `false`：完全回退阶段1 行为（灰度/线上兜底）。
 */
const M2_CLUSTER_TEMPLATE = true;

/**
 * Round25 · C2 摊牌放大牌扇的半尺寸（与 layout.css `.felt__cards--showdown .pcard--sm` 逐档口径一致）：
 * fan 宽 = 2×卡宽 + 2px gap → 半宽 = 卡宽 + 1；半高 = 卡高 / 2。
 * 用途：摊牌放大态把牌扇**沿背离底池方向平移**（enlarged 半尺寸 − solver 冻结的常态牌背 OBB 半尺寸），
 * 使放大后牌扇「近中心带一侧」的边缘与常态牌背对齐 —— 常态牌背已由 solver 让出受保护中心带 8px，
 * 故放大牌不再越 board 顶沿（真机 flaky：A2 摊牌牌 ∩ 桌心带；尤见 3 人桌椭圆顶弧 pod 贴小 board）。
 */
const SHOWDOWN_FAN_HALF: Record<SeatDensity, { hw: number; hh: number }> = {
  full: { hw: 35, hh: 24 },
  compact: { hw: 35, hh: 24 },
  row: { hw: 29, hh: 20 },
  micro: { hw: 25, hh: 17 },
};

/**
 * 问题1（毛毡拆层安全环）：视觉毛毡 `.felt__surface` 相对牌桌容器四周内缩的 `--felt-inset-*`
 * （设计 §4.1 收敛表）。内缩量 = 半个 pod + edge(8)，随密度档下发（与 `--seat-w/--seat-h` 同口径、
 * 单一真源）：顶底 56/52/46/40、左右 44/41/38/36（full-p → micro-p）。CSS 由
 * `.felt--stack-ring .felt__surface { inset: var(--felt-inset-block) var(--felt-inset-inline) }` 消费。
 * 与席位坐标解耦（planRing 的 box/shape 仍从 `.felt` 量测、不受 surface 影响 → 零几何回归）。
 */
const FELT_INSET: Record<SeatDensity, Record<string, string>> = {
  full: { '--felt-inset-block': '56px', '--felt-inset-inline': '44px' },
  compact: { '--felt-inset-block': '52px', '--felt-inset-inline': '41px' },
  row: { '--felt-inset-block': '46px', '--felt-inset-inline': '38px' },
  micro: { '--felt-inset-block': '40px', '--felt-inset-inline': '36px' },
};

/**
 * 旧的固定百分比椭圆坐标。
 *
 * 它已经不再是布局依据（第 5 轮 P0：固定半径与 px 卡片尺寸、人数、容器高度全部脱钩，
 * 10 人桌必然重叠），仅保留为「首帧尚未拿到实测几何」时的兜底，
 * 以及历史口径的对照基线（round2-visual 单测）。
 */
export function seatPositions(n: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2 + Math.PI / 2; // 从正下方开始
    out.push({
      x: 50 + 43 * Math.cos(t),
      y: 52 + 40 * Math.sin(t),
    });
  }
  return out;
}

/** 席位层的实测几何（全部为席位层坐标，单位 px）。 */
interface FeltGeom {
  box: Size;
  me: Size;
  band: { top: number; bottom: number; halfWidth: number } | null;
  blockers: Rect[];
  shape: { rx: number; ry: number } | null;
  /**
   * Round21-M0 关键区实测（供 assertProtectedRegions 度量；不改 solver blockers 口径）：
   * `boardSlots` = 真实公共牌 5 槽（A）；`heroCards` = 英雄手牌逐张（B）；`heroCat` = 牌型提示（C）；
   * `seatRegions` = 逐席 `.seat__made`（D）/`.seat__badges`（E）；`panels` = 全部浮层/页脚面板（F）。
   */
  boardSlots: Rect[];
  heroCards: Rect[];
  heroCat: Rect | null;
  seatRegions: Map<number, { made?: Rect; badges?: Rect }>;
  panels: Rect[];
}

const r1 = (v: number) => Math.round(v * 2) / 2;

/** Round21-M0：M0 度量收全的浮层/页脚面板选择器（设计 N-1；仅供 panel 关键区度量，不进 solver blockers）。 */
const PANEL_SELECTORS = '.settle, .drawer, .modal, .scoretip__detail, .eqshell, .toast, .sizer';

/** 读取一次牌桌几何；返回 null 表示还没有可用尺寸（首帧 / 隐藏态）。 */
function measureFelt(felt: HTMLElement): FeltGeom | null {
  const box = { w: felt.clientWidth, h: felt.clientHeight };
  if (box.w <= 0 || box.h <= 0) return null;
  const cs = getComputedStyle(felt);
  const bl = parseFloat(cs.borderLeftWidth) || 0;
  const bt = parseFloat(cs.borderTopWidth) || 0;
  const fr = felt.getBoundingClientRect();
  const ox = fr.left + bl;
  const oy = fr.top + bt;
  const rel = (r: DOMRect): Rect => ({ left: r.left - ox, top: r.top - oy, right: r.right - ox, bottom: r.bottom - oy });
  const relClamp = (r: DOMRect): Rect => {
    const q = rel(r);
    return { left: r1(q.left), top: r1(q.top), right: r1(q.right), bottom: r1(q.bottom) };
  };
  const nonEmpty = (r: DOMRect): boolean => r.width > 0 && r.height > 0;

  const meEl = felt.querySelector('.felt__seat--me .seat');
  const meR = meEl ? (meEl as HTMLElement).getBoundingClientRect() : null;
  const me = meR && meR.width > 0 ? { w: r1(meR.width), h: r1(meR.height) } : { w: 176, h: 170 };

  const centerEl = felt.querySelector('.felt__center');
  let band: FeltGeom['band'] = null;
  if (centerEl) {
    const q = rel((centerEl as HTMLElement).getBoundingClientRect());
    band = {
      top: r1(q.top),
      bottom: r1(q.bottom),
      halfWidth: r1(Math.max(Math.abs(q.left - box.w / 2), Math.abs(q.right - box.w / 2))),
    };
  }

  // 圆角：`border-radius: 200px / 140px` → padding box 内的椭圆角要减掉描边宽度
  const rad = cs.borderTopLeftRadius.split(' ');
  const rx = Math.max(0, (parseFloat(rad[0]) || 0) - bl);
  const ry = Math.max(0, (parseFloat(rad[1] ?? rad[0]) || 0) - bt);
  const shape = rx > 0 && ry > 0 ? { rx: Math.min(rx, box.w / 2), ry: Math.min(ry, box.h / 2) } : null;

  // 外部浮层：只把与毛毡真正有交集的部分当作障碍（lg 断点赢率浮层压在毛毡右下角）
  const blockers: Rect[] = [];
  document.querySelectorAll('.eqshell, .sizer').forEach((el) => {
    const r = (el as HTMLElement).getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    const q = rel(r);
    if (q.right <= 0 || q.bottom <= 0 || q.left >= box.w || q.top >= box.h) return;
    blockers.push({
      left: r1(Math.max(0, q.left)),
      top: r1(Math.max(0, q.top)),
      right: r1(Math.min(box.w, q.right)),
      bottom: r1(Math.min(box.h, q.bottom)),
    });
  });

  // ---- Round21-M0 关键区实测（A/B/C/D/E/F）----
  // A：真实公共牌 5 槽（`.board` 的直接子元素：占位 `.board__slot` 或已发牌 `.pcard`）。
  const boardSlots: Rect[] = [];
  felt.querySelectorAll('.board > *').forEach((el) => {
    const r = (el as HTMLElement).getBoundingClientRect();
    if (nonEmpty(r)) boardSlots.push(relClamp(r));
  });
  // B：英雄手牌逐张（面朝上 `.pcard`，正立 rot=0）。
  const heroCards: Rect[] = [];
  felt.querySelectorAll('.felt__seat--me .seat__cards .pcard').forEach((el) => {
    const r = (el as HTMLElement).getBoundingClientRect();
    if (nonEmpty(r)) heroCards.push(relClamp(r));
  });
  // C：英雄牌型提示 `.hero__cat`。
  const catEl = felt.querySelector('.felt__seat--me .hero__cat');
  const catR = catEl ? (catEl as HTMLElement).getBoundingClientRect() : null;
  const heroCat = catR && nonEmpty(catR) ? relClamp(catR) : null;
  // D/E：逐席 `.seat__made` / `.seat__badges`（按 data-seat 归属）。
  const seatRegions = new Map<number, { made?: Rect; badges?: Rect }>();
  felt.querySelectorAll('.felt__seat[data-seat]').forEach((seatEl) => {
    const seat = Number((seatEl as HTMLElement).dataset.seat);
    if (!Number.isFinite(seat)) return;
    const entry: { made?: Rect; badges?: Rect } = {};
    const madeEl = seatEl.querySelector('.seat__made');
    if (madeEl) {
      const r = (madeEl as HTMLElement).getBoundingClientRect();
      if (nonEmpty(r)) entry.made = relClamp(r);
    }
    const badgesEl = seatEl.querySelector('.seat__badges');
    if (badgesEl) {
      const r = (badgesEl as HTMLElement).getBoundingClientRect();
      if (nonEmpty(r)) entry.badges = relClamp(r);
    }
    if (entry.made || entry.badges) seatRegions.set(seat, entry);
  });
  // F：全部浮层/页脚面板（与毛毡有交集的部分；仅度量，不进 solver blockers）。
  const panels: Rect[] = [];
  document.querySelectorAll(PANEL_SELECTORS).forEach((el) => {
    const r = (el as HTMLElement).getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return;
    const q = rel(r);
    if (q.right <= 0 || q.bottom <= 0 || q.left >= box.w || q.top >= box.h) return;
    panels.push({
      left: r1(Math.max(0, q.left)),
      top: r1(Math.max(0, q.top)),
      right: r1(Math.min(box.w, q.right)),
      bottom: r1(Math.min(box.h, q.bottom)),
    });
  });

  return { box: { w: r1(box.w), h: r1(box.h) }, me, band, blockers, shape, boardSlots, heroCards, heroCat, seatRegions, panels };
}

/** 对手区（stack 布局）可用内容盒。 */
function measureSeatsBox(felt: HTMLElement): Size | null {
  const layer = felt.querySelector('.felt__seats');
  if (!layer) return null;
  const el = layer as HTMLElement;
  if (el.clientWidth <= 0 || el.clientHeight <= 0) return null;
  return { w: r1(el.clientWidth), h: r1(el.clientHeight) };
}

export function Table({
  c,
  game,
  compactSeats,
  layout,
  revealAll,
  clockTotalMs,
  winningCards = null,
}: {
  c: GameController;
  game: Game;
  /** 首帧兜底档位提示（实测几何到位后由算法接管）。 */
  compactSeats: boolean;
  /** 席位布局：环形（宽屏）或纵向三段（窄屏）。 */
  layout: SeatLayout;
  revealAll: boolean;
  /** 行动时限总时长（ms），0 表示未开启。 */
  clockTotalMs: number;
  /** R3/R5：结算时需高亮的制胜 5 张牌（公共牌 + 赢家底牌）；null 表示不高亮。 */
  winningCards?: readonly number[] | null;
}) {
  const feltRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<FeltGeom | null>(null);
  const [seatsBox, setSeatsBox] = useState<Size | null>(null);

  /**
   * QA-BUG-R5-DRIFT（R5 防漂移）：座位求解的几何输入**按手锁定**。
   *
   * 缺陷根因：`.felt` / `.felt__seats` 的实测盒（席位算法的输入）与页脚同处一条弹性骨架，
   * 于是「打开加注 sizer / 做一次行动」这类同手内的 chrome 变化会压缩容器高度，导致
   * `planRing` / `planGrid` 用变小的盒子重排、甚至降档，全部对手席位坐标漂移
   * （wide 上移 74px、portrait 由 189×124 收缩为 125×52）。
   *
   * 修法：几何输入只在「布局纪元」变化时重新采集，同一纪元内**冻结**为该手开始时的静止值。
   * 纪元键 = 手号 × 布局(oval/stack) × 对手数 × 视口尺寸 —— 只有换手 / 切布局 / 淘汰改人数 /
   * 真实视口 resize 才重解；sizer 展开、我方行动后本街投入多一行、页脚增高等都不改变纪元键，
   * 因此不再触发重排（配合 layout.css 让 sizer 走浮层、`.felt__seats` 顶对齐，坐标严格守恒）。
   */
  const epochRef = useRef('');
  const capturedEpoch = useRef('');

  const measure = useCallback(() => {
    const felt = feltRef.current;
    if (!felt) return;
    /* 纪元未变且已采集到有效几何 → 冻结，忽略 chrome 高度变化引发的重测（防漂移核心）。 */
    if (capturedEpoch.current === epochRef.current && geom !== null) return;
    const g = measureFelt(felt);
    const b = measureSeatsBox(felt);
    if (!g && !b) return; // 首帧未布局，等下一帧再采
    capturedEpoch.current = epochRef.current;
    setGeom(g);
    setSeatsBox(b);
  }, [geom]);

  // 每次渲染都重测（只有数值变化才 setState，因此不会自激）；外加容器尺寸与窗口变化监听
  useLayoutEffect(() => {
    measure();
  });

  /**
   * 真实视口 resize：清掉「已采集纪元」，强制按当前视口重新求解（响应式仍然生效）。
   * 与之相对，`.felt` / `.felt__seats` / `.sizer` 的 ResizeObserver 只触发 `measure()`，
   * 而 `measure()` 在同一纪元内是冻结的 —— 于是 sizer 展开 / 行动导致的容器高度变化被忽略，
   * 但浏览器窗口尺寸变化仍会重排。
   */
  const remeasureNow = useCallback(() => {
    capturedEpoch.current = '';
    measure();
  }, [measure]);

  useLayoutEffect(() => {
    const felt = feltRef.current;
    if (!felt || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', remeasureNow);
      return () => window.removeEventListener('resize', remeasureNow);
    }
    const ro = new ResizeObserver(() => measure());
    ro.observe(felt);
    const layer = felt.querySelector('.felt__seats');
    if (layer) ro.observe(layer);
    document.querySelectorAll('.eqshell, .sizer').forEach((el) => ro.observe(el));
    window.addEventListener('resize', remeasureNow);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', remeasureNow);
    };
  }, [measure, remeasureNow, layout, game.players.length]);

  const oppCount = game.players.filter((p) => p && !p.isHuman).length;

  /**
   * 竖屏公共牌带宽度 —— 竖屏环布几何的**单一真源**（DEF-01 / DEF-02 根因修复）。
   *
   * 两侧各预留「edge(8) + full-p pod(72) + gap(8) + 8 余量 = 96」的 pod 通道，使最大 pod 也能
   * 贴边落在中心带避让区之外；据此下发 `--board-max`（公共牌带定宽，见 CSS `.felt--stack-ring .board`），
   * **并用同一个 boardMax 反推「渲染后」中心带半宽**喂给 `planRing`。
   *
   * 关键：不再用「首帧冻结实测」的 `geom.band.halfWidth`。首帧公共牌带尚未定宽（`--board-max` 依赖
   * ring→stackRing，存在一帧空窗）会坍缩成窄条，冻结后该窄值让算法**少留**中心带 → 定宽后的真实
   * 中心带被侧列 pod 侵入（DEF-02）。改用 boardMax 反推的确定半宽，算法避让区 = 真实渲染几何。
   */
  const portraitBoardMax = geom ? Math.max(140, geom.box.w - 2 * 96) : 0;
  /** 渲染后中心带半宽：`.felt__center` border-box = board 宽 + padding-inline(4×2)。 */
  const portraitBandHalf = (portraitBoardMax + 8) / 2;

  /**
   * 布局纪元键：只在「换手 / 切布局 / 对手数变化 / 竖屏环布生效 / 真实视口 resize」时改变。
   * measure() 用它决定是否重新采集几何（见 epochRef 注释）。视口尺寸取整到 2px，
   * 避免滚动条/像素抖动引起的无意义重解。
   *
   * 纳入 `stackRing`（在 stackRing 定义后赋值）：竖屏环布存在「首帧 geom=null → 无 ring 样式 →
   * 测得非环布态几何」再「次帧套上 `.felt--stack-ring` + `--board-max` → 中心带跳到 46%H」的一帧空窗。
   * 若不把 stackRing 计入纪元，首帧那份「中心带在别处」的几何会被冻结，planRing 避让错位置 →
   * 定宽后的真实中心带被侧列 pod 侵入（DEF-02）。计入后 false→true 触发恰好一次重测，用环布态真实几何求解。
   */
  const vw = typeof window !== 'undefined' ? Math.round(window.innerWidth / 2) * 2 : 0;
  const vh = typeof window !== 'undefined' ? Math.round(window.innerHeight / 2) * 2 : 0;

  /**
   * 环形方案：
   *   - `oval`（宽屏）：宽屏 `DENSITIES` 档，席位沿环形轨道分布；
   *   - `stack` 且**竖屏**（实测 box 高 ≥ 宽）：需求三竖屏椭圆整周环布，用 `DENSITIES_PORTRAIT`
   *     的小 pod 盒（72×96…56×64），把对手分布到左列 + 顶弧 + 右列、英雄底部中央。
   *     受保护中心带（公共牌 + 底池）的半宽用 `portraitBandHalf`（= 由下发的 `--board-max` 反推的
   *     真实渲染半宽）夹定，与 CSS 定宽严格一致，使侧列 pod 落在中心带避让区之外（AC3-1 / AC3-9）；
   *     装不下的档位由 `validateRing` 判失败自动降档，绝不重叠、不侵入。
   * `stack` 且横屏（矮视口，box 宽 > 高）不在本轮环布范围（PRD §3.2 Out of Scope），仍走 planGrid。
   */
  const ring: RingPlan | null = useMemo(() => {
    if (!geom || oppCount <= 0) return null;
    if (layout === 'oval') {
      return planRing({
        box: geom.box,
        count: oppCount,
        me: geom.me,
        band: geom.band,
        blockers: geom.blockers,
        shape: geom.shape,
      });
    }
    if (layout === 'stack' && geom.box.h >= geom.box.w) {
      /**
       * 中心带避让在竖屏用「加高的保护带」喂给 planRing（DEF-02 复测 · 320×568 残留）：
       * `.felt__center` 的**竖向位置在一手之内会随街推进上移**（实测翻牌带顶≈183 → 河牌带顶≈169，
       * 约 14px），而席位只按纪元首测的 band 规划一次、街切换不重排（重排会让 pod 逐街跳动）。
       * 390/844 余量大能吸收此漂移，但 320×568 顶弧 pod 与带顶仅约 8px 间隙时会被上移的带追上叠 1px。
       * 因此把规划用 band 的**顶沿上抬 `BAND_DRIFT`**，为「带在本手内上移」预留净空 → 顶弧 pod 规划得更高，
       * 带即便上移也落进 pod 让出的空档，不再侵入（AC3-1 / AC3-9）。底沿不动（其下即英雄，另有避让）。
       */
      const BAND_DRIFT = 20;
      const band = geom.band
        ? { ...geom.band, top: geom.band.top - BAND_DRIFT, halfWidth: portraitBandHalf }
        : null;
      /**
       * 竖屏档位随人数封顶（设计 4.2.1）：2–4 人 full-p、5–6 compact-p、7–8 row-p、9–10 micro-p。
       * 不再一律从 full 起降档 —— 人多时 full-p（72×96）虽能几何塞下、却把「pod→底池」的环内侧
       * 让位区挤没，下注 chip 无处解耦（DEF-04）。按人数从对应档起切片喂给 planRing（仍保留其后
       * 各档作降档兜底），人多用小 pod 腾出环内侧走廊，chip 才能落在 pod 与中心带之间不重叠。
       */
      const startIdx = oppCount <= 3 ? 0 : oppCount <= 5 ? 1 : oppCount <= 7 ? 2 : 3;
      return planRing({
        box: geom.box,
        count: oppCount,
        me: geom.me,
        band,
        blockers: geom.blockers,
        shape: geom.shape,
        densities: DENSITIES_PORTRAIT.slice(startIdx),
      });
    }
    return null;
  }, [layout, geom, oppCount, portraitBandHalf]);

  /** 竖屏椭圆环布是否生效（实测几何到位且为竖屏 stack）。 */
  const stackRing = layout === 'stack' && ring !== null;

  /* 纪元键在 stackRing 确定后赋值（含 stackRing，见上方说明）：环布首次生效时触发一次重测。 */
  epochRef.current = `${game.handNumber}|${layout}|${oppCount}|${vw}x${vh}|${stackRing ? 'ring' : 'noring'}`;

  /** 网格方案（stack 横屏矮视口 / 首帧几何未到位的兜底）。 */
  const grid = useMemo(() => {
    if (layout !== 'stack' || !seatsBox || oppCount <= 0) return null;
    // 竖屏已由 planRing 接管，横屏矮视口仍用网格（本轮不改横屏，PRD §3.2）。
    if (geom && geom.box.h >= geom.box.w) return null;
    /*
     * 列数上限跟着实测宽度走（REG2-NEW-002）：竖屏 360–390 宽最多 4 列，
     * 而横屏矮视口宽度 640–844、高度只有 360–390 —— 此时"多列少行"才放得下，
     * 卡在 4 列会强行排出 3 行、行高被压到 MIN_ROW_H 以下而 `feasible=false`。
     * 阈值按 micro 档最小列宽反解：6 列需要 6×84 + 5×4 = 524，560 已留足余量。
     */
    return planGrid({ box: seatsBox, count: oppCount, maxColumns: seatsBox.w >= 560 ? 6 : 4 });
  }, [layout, geom, seatsBox, oppCount]);

  const density: SeatDensity = ring?.density ?? grid?.tier ?? (compactSeats ? 'compact' : 'full');

  const fallback = useMemo(() => seatPositions(game.players.length), [game.players.length]);

  const history = game.handHistory();
  const lastRec = history[history.length - 1];
  const showdownReveals =
    game.phase === 'handComplete' && lastRec?.handNumber === game.handNumber
      ? (lastRec.events.find((e) => e.kind === 'showdown') as { kind: 'showdown'; reveals: readonly { seat: number; hole: readonly [number, number]; value: number }[] } | undefined)
      : undefined;
  const revealMap = new Map<number, number>();
  showdownReveals?.reveals.forEach((r) => revealMap.set(r.seat, r.value));

  /**
   * Round23 FR-4：某席在「全员亮牌」态应展示的牌型文案（展示层现算，不改引擎）。
   *   - 摊牌保留手（revealMap 命中）：英雄给完整读牌文案，其余给牌型名（与既有口径一致）；
   *   - revealAll 下的弃牌者 / 其它未摊牌者：用 `evaluateHoleAndBoard` **现算**成手牌型名（灰阶显示），
   *     board < 3 张（翻前弃牌）无法成手则返回 null（只亮底牌、不崩溃，AC-4）。
   */
  const revealValueOf = (seat: number): string | null => {
    const p = game.players[seat];
    if (!p) return null;
    const mapped = revealMap.get(seat);
    if (mapped !== undefined) {
      return p.isHuman ? describeHandValue(mapped).text : describeHandValue(mapped).categoryName;
    }
    if (revealAll && p.hole && game.board.length >= 3) {
      try {
        const v = evaluateHoleAndBoard(p.hole, game.board.slice(0, 5));
        return describeHandValue(v).categoryName;
      } catch {
        return null;
      }
    }
    return null;
  };

  const clock = c.clockRemainingMs;

  /** FR-5：英雄牌型提示文案（展示层派生，翻牌前兜底；随 board 刷新）。 */
  const heroCat = c.heroCategory();

  // R3/R5：制胜 5 张的高亮集合（结算时才非空）。公共牌与赢家底牌都按这个集合上高亮描边。
  const winSet = useMemo(() => new Set(winningCards ?? []), [winningCards]);

  const seats = game.players.map((_, seat) => seat);
  const mySeats = seats.filter((s) => game.players[s]?.isHuman);
  const others = seats.filter((s) => game.players[s] && !game.players[s]?.isHuman);
  /** 对手在环形轨道上的次序 = players 的座位次序（顺时针，从「我」的左手边起）。 */
  const oppOrder = new Map<number, number>(others.map((s, i) => [s, i]));

  /**
   * R11-2：当前手的 SB / BB / D 角色位（读引擎 `handStart`，不改口径）。
   * dead SB（小盲座位玩家已不在手内）不渲染 SB —— 与引擎「死盲不投注」一致。
   */
  const roles = game.currentRoles();

  const renderSeat = (seat: number): ReactElement | null => {
    const p = game.players[seat];
    if (!p) return null;
    const reveal = p.isHuman || revealAll || revealMap.has(seat);

    /**
     * 定位口径：
     *   - `oval` 或**竖屏 stack-ring**（`ring` 非空）：真人与对手都吃**同一套 px 坐标**（算法输出），
     *     真人显式清掉 CSS 兜底的 `bottom`，避免 top/bottom 同时生效把卡片拉伸；对手落算法环布点。
     *   - `oval` 首帧（几何未知、ring 为 null）：对手退回旧百分比椭圆、真人退回 CSS 贴底，视觉不空窗。
     *   - `stack` 横屏矮视口（ring 为 null、走 grid）：席位是网格流内元素，不下发任何坐标。
     */
    let style: CSSProperties | undefined;
    if (ring) {
      if (p.isHuman) {
        /**
         * QA-BUG-R25-HEROCLIP：竖屏环布英雄**底沿锚定**到 felt 实时底边（`bottom:var(--space-2)`
         * = solver 的 edge=8），而非用冻结的 `ring.me.y`（top 锚定）。
         *
         * 根因：席位几何按手冻结（防漂移），但 `.felt`(height:100% + max-height:500) 的高度会随页脚
         * 内容在一手内变化（发牌时短页脚 → felt≈496；轮到英雄行动 / sizer 展开时页脚增高 → felt≈456）。
         * 冻结的 `me.y = H-edge-meH/2` 按 H=496 解出，felt 收缩到 456 后英雄 pod 底沿越过 felt 底、被
         * `.gs__main`(overflow:hidden) 裁掉 ~22px（真机 flaky：A1 英雄越 felt、A5 行动 pill 可见比 0~0.125）。
         * 底沿锚定让英雄恒贴当前 felt 底、向上生长，H 冻结值再也不影响其可见性（对齐 `.felt--oval` 英雄口径）。
         */
        style = stackRing
          ? { left: `${ring.me.x}px`, top: 'auto', bottom: 'var(--space-2)', transform: 'translateX(-50%)' }
          : { left: `${ring.me.x}px`, top: `${ring.me.y}px`, bottom: 'auto', transform: 'translate(-50%, -50%)' };
      } else {
        const idx = oppOrder.get(seat) ?? 0;
        const pt = ring.seats[idx];
        if (pt) style = { left: `${pt.x}px`, top: `${pt.y}px` };
      }
    } else if (layout === 'oval' && !p.isHuman) {
      style = { left: `${fallback[seat]?.x ?? 50}%`, top: `${fallback[seat]?.y ?? 50}%` };
    }

    /**
     * FR-2：竖屏环布对手手牌扇形「朝桌内侧（底池方向）」（与 chip / dealer 同一 pod→底池射线口径）：
     * 按 pod 屏幕横向分区判定——左列（cx<42%）扇形贴 pod 右缘、右列（cx>58%）贴左缘、
     * 顶弧（居中）朝底池即贴 pod 下缘（'bottom'）——避免与骑在 pod 上沿的头像相撞。
     * 交给 SeatCard 落到 `.seat__cards[data-inner-side]`（absolute 化，与 pod 内主元素两两不重叠）。
     */
    let innerSide: 'left' | 'right' | 'bottom' | undefined;
    if (stackRing && ring && geom && !p.isHuman) {
      const idx = oppOrder.get(seat) ?? 0;
      const pt = ring.seats[idx];
      if (pt) {
        const W = geom.box.w;
        innerSide = pt.x < W * 0.42 ? 'right' : pt.x > W * 0.58 ? 'left' : 'bottom';
      }
    }

    return (
      <div key={seat} data-seat={seat} className={`felt__seat${p.isHuman ? ' felt__seat--me' : ''}`} style={style}>
        <SeatCard
          player={p}
          isButton={game.buttonSeat === seat}
          isSmallBlind={roles?.sbSeat === seat && !!game.players[seat]?.inHand}
          isBigBlind={roles?.bbSeat === seat}
          isToAct={game.toAct === seat}
          thinking={c.thinkingSeat === seat}
          isWinner={c.winners.has(seat)}
          bubble={c.bubbles.get(seat) ?? null}
          stats={c.stats.view(seat)}
          style={c.styles[seat] ?? null}
          reveal={reveal}
          showdownValue={revealValueOf(seat)}
          winningCards={winSet}
          clockRatio={p.isHuman && clock !== null && clockTotalMs > 0 ? clock / clockTotalMs : null}
          /**
           * 档位只作用于对手：
           *   1. 「我」的底牌 / 筹码 / 本街投入是决策必需信息，任何视口都不降档；
           *   2. 更硬的原因是**避免量测自激**——我的卡高参与「对手区可用高度」的计算，
           *      若它随档位变化，就会出现「档位→我的卡高→可用高度→档位」的环，
           *      实测 min360 直接触发 React #185（Maximum update depth）。
           */
          density={p.isHuman ? 'full' : density}
          /**
           * 竖屏环布（stack-ring）下对手的「本街投入」从 pod 内摘出，改由 `.felt__chips`
           * 独立绝对定位在「席位→底池」连线上（AC3-3 chip 解耦），避免注额标签压在 pod
           * 或相邻席位上。真人仍保留卡内幽灵占位行（防漂移，见 SeatCard trailingBet）。
           */
          betDecoupled={stackRing && !p.isHuman}
          /**
           * 阶段1 统一占位：竖屏环布下对手的手牌牌背与 SB/BB 徽标改由 Table 的 felt 级
           * `.felt__cards` / `.felt__blinds` 层按 solver 下发坐标渲染（统一占位表，AC-L2/L5/L6）。
           * 此处置真，令 SeatCard 内对手 pod 不再渲染 `.seat__cards` / `.seat__badges`（避免双份 / CSS 锚定复发）。
           * 英雄不受影响（手牌 + 牌型提示 + 自身盲注仍在 pod 内）。
           */
          satellitesDecoupled={stackRing && !p.isHuman}
          innerSide={innerSide}
          categoryLabel={p.isHuman ? heroCat : null}
        />
      </div>
    );
  };

  /** 档位与网格参数下发给 CSS：卡片盒尺寸必须与算法标称值严格一致。 */
  const feltStyle = {
    ...(ring ? { '--seat-w': `${ring.card.w}px`, '--seat-h': `${ring.card.h}px` } : {}),
    ...(grid ? { '--seat-cols': String(grid.columns), '--seat-row-h': `${grid.rowH}px`, '--seat-gap': `${grid.gap}px` } : {}),
    /**
     * 竖屏环布：公共牌带定宽 = `portraitBoardMax`（两侧各留 96px pod 通道，见上方单一真源），
     * 使实测中心带半宽 = portraitBandHalf 恰等于喂给 planRing 的避让半宽 → 侧列 pod 严格落在
     * 中心带之外（AC3-1 / AC3-9）。CSS `.felt--stack-ring .board { width: var(--board-max) }` 消费。
     */
    ...(stackRing && geom ? { '--board-max': `${portraitBoardMax}px` } : {}),
    /**
     * 问题1（毛毡拆层安全环）：视觉毛毡 `.felt__surface` 相对牌桌容器四周内缩 `--felt-inset-*`，
     * 使「对手 pod 中心骑在 surface 椭圆边缘」的同时 pod 外接盒完整落在容器内（0 裁切，设计 §4.1）。
     * 内缩量 = 半个 pod + edge(8)，随密度档下发（与 `--seat-w/--seat-h` 同口径，单一真源）：
     * full-p 顶底56/左右44、compact-p 52/41、row-p 46/38、micro-p 40/36。缺省兜底见 CSS。
     * 与席位坐标解耦（planRing 的 box/shape 仍从 `.felt` 量测、不受 surface 影响 → 零几何回归）。
     */
    ...(stackRing && ring ? FELT_INSET[ring.density] : {}),
  } as CSSProperties;

  const planTag = ring
    ? `ring:${density}:${ring.feasible ? 'fit' : 'tight'}`
    : grid
      ? `grid:${density}:${grid.columns}x${grid.rows}:${grid.feasible ? 'fit' : 'tight'}`
      : undefined;

  /**
   * 统一占位求解（阶段1 / 方案 B，设计方案 §3）。
   *
   * 把原先分裂的三段独立射线扫描（chipDots / actPills / dealer）与「牌背 / 盲注徽标 CSS 边缘锚定」
   * 全部并入**同一个** `solveOverlayLayout`：pod、牌背（±6° 计入 OBB）、chip、pill、SB/BB/D badge、
   * 中心带、底池、公共牌、英雄排他区、外部浮层进同一坐标系与同一碰撞表，用 OBB/SAT 判交、白名单
   * 阈值管控，按「badge/D > cardback > chip > pill」优先级 + 短射线席位优先放置，无解走声明式降级，
   * 结构性杜绝「pill 压牌背 / D 贴牌背 / chip 顶部堆叠」（AC-L1~L8）。产出 `LayoutSnapshot`，
   * 各卫星由 felt 级独立绝对层按 solver 下发坐标渲染（不占布局流 → 防漂移）。
   * band 不可用（首帧）则不求解，卫星层为空、视觉不空窗（对手 pod 仍在）。
   */
  const overlay = useMemo(() => {
    if (!stackRing || !ring || !geom?.band) return null;
    const W = geom.box.w;
    const P = { x: W / 2, y: (geom.band.top + geom.band.bottom) / 2 };
    const podHW = ring.card.w / 2;
    const podHH = ring.card.h / 2;
    const seatsRuntime: SeatRuntime[] = [];
    // Rect → OBB（层坐标，rot=0；关键区实测盒转碰撞盒，口径「渲染盒 ≤ 度量盒」）。
    const rectToObb = (r: Rect): OBB => ({
      cx: (r.left + r.right) / 2,
      cy: (r.top + r.bottom) / 2,
      hw: (r.right - r.left) / 2,
      hh: (r.bottom - r.top) / 2,
      rot: 0,
    });
    for (const seat of others) {
      const p = game.players[seat];
      if (!p) continue;
      const idx = oppOrder.get(seat) ?? 0;
      const pt = ring.seats[idx];
      if (!pt) continue;
      const innerSide: 'left' | 'right' | 'bottom' = pt.x < W * 0.42 ? 'right' : pt.x > W * 0.58 ? 'left' : 'bottom';
      const b = c.bubbles.get(seat);
      const info = b ? pillInfo(b.kind, p) : null;
      const reg = geom.seatRegions.get(seat);
      seatsRuntime.push({
        seat,
        pod: { cx: pt.x, cy: pt.y, hw: podHW, hh: podHH, rot: 0 },
        innerSide,
        inHand: p.inHand,
        folded: p.folded,
        // Round25 · C2：单机全揭示态下已弃牌者也产出牌背槽位，摊牌 face-up 由统一 solver 锚点承载（无独立放大层）。
        revealAll,
        streetContributed: p.streetContributed,
        actionKind: info ? (b!.kind as SeatRuntime['actionKind']) : null,
        // SB/BB 与 SeatCard 同口径：SB 需在手（dead SB 不显），BB 随本手角色位。
        blind: roles?.sbSeat === seat && p.inHand ? 'SB' : roles?.bbSeat === seat ? 'BB' : null,
        isDealer: game.buttonSeat === seat,
        // Round21-M0：D/E 类关键区（实测；缺省则该席不产出）。
        made: reg?.made ? rectToObb(reg.made) : undefined,
        badges: reg?.badges ? rectToObb(reg.badges) : undefined,
      });
    }
    const heroPod: OBB = { cx: ring.me.x, cy: ring.me.y, hw: geom.me.w / 2, hh: geom.me.h / 2, rot: 0 };
    const blockers: OBB[] = geom.blockers.map(rectToObb);
    const input: OverlayInput = {
      box: geom.box,
      ring,
      pot: P,
      band: { top: geom.band.top, bottom: geom.band.bottom, halfWidth: portraitBandHalf },
      // Round21-M0：英雄手牌逐张（B）+ 牌型提示（C）真实注入。
      hero: {
        pod: heroPod,
        cat: geom.heroCat ? rectToObb(geom.heroCat) : undefined,
        cards: geom.heroCards.length ? geom.heroCards.map(rectToObb) : undefined,
      },
      seats: seatsRuntime,
      blockers,
      // Round21-M0：A 类真实公共牌 5 槽 + F 类浮层/页脚面板（仅度量，不进 solver occupancy）。
      boardSlots: geom.boardSlots.length ? geom.boardSlots.map(rectToObb) : undefined,
      panels: geom.panels.length ? geom.panels.map(rectToObb) : undefined,
    };
    // 阶段2（M2）：优先走席位簇模板构造（纯映射 + §5.3 降级），构造后自校验；
    // 任何异常 / 校验不过一律回退阶段1 求解器（PRD §10 回退开关 —— 同一 LayoutSnapshot 结构）。
    if (M2_CLUSTER_TEMPLATE) {
      try {
        const cplan = planClusters(input);
        const snap = clusterPlanToSnapshot(cplan, input);
        if (validateClusters(cplan, input).ok) return snap;
      } catch {
        /* 落入下方阶段1 回退 */
      }
    }
    // 阶段1 回退基座：M1 求解器保证零重叠 / 保护区，但其 ±6° 侧列牌背等瞬态可能残留 M2 归属歧义
    // （κ>0.6 / 贴靠越界）。回退分支对快照施加 enforceAttribution 兜底：按 pill→chip→cardback→badge
    // 优先级删除违例瞬态卫星并记 `:hidden`，确保**任何**渲染路径都不产出归属歧义（PRD §10 归属硬约束）。
    const fallback = solveOverlayLayout(input);
    enforceAttribution(fallback.elements, fallback.degradations, fallback.box, fallback.density);
    return fallback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackRing, ring, geom, others, oppOrder, game.players, game.buttonSeat, roles, c.bubbles, portraitBandHalf, revealAll]);

  /** 下注 chip 派生：solver 已保证不叠 pod / 不进中心带 / 两两分离（AC3-3 / AC-L4）。 */
  const chipDots = useMemo(() => {
    if (!overlay) return [];
    const out: { seat: number; x: number; y: number; amount: number }[] = [];
    for (const el of overlay.elements) {
      if (el.kind !== 'chip' || el.seat === null) continue;
      const p = game.players[el.seat];
      if (!p) continue;
      out.push({ seat: el.seat, x: el.obb.cx, y: el.obb.cy, amount: p.streetContributed });
    }
    return out;
  }, [overlay, game.players]);

  /** 操作 pill 派生：文案/配色由 `pillInfo`（展示层）映射，坐标来自 solver（AC3-3 / AC-L2 pill×牌背=0）。 */
  const actPills = useMemo(() => {
    if (!overlay) return [];
    const out: { seat: number; x: number; y: number; text: string; cls: string }[] = [];
    for (const el of overlay.elements) {
      if (el.kind !== 'pill' || el.seat === null) continue;
      const pl = game.players[el.seat];
      if (!pl) continue;
      const b = c.bubbles.get(el.seat);
      const info = b ? pillInfo(b.kind, pl) : null;
      if (!info) continue;
      out.push({ seat: el.seat, x: el.obb.cx, y: el.obb.cy, text: info.text, cls: info.cls });
    }
    return out;
  }, [overlay, game.players, c.bubbles]);

  /**
   * 对手手牌牌背派生（AC-L2 / AC-L6）：坐标 + 旋转由 solver 下发（牌背 ±6° 已计入 OBB），
   * 由 felt 级 `.felt__cards` 独立层渲染，不再靠 SeatCard 内 CSS 边缘锚定 → 与 pill/chip/D 同一
   * 占位表，结构性不相撞。folded / 未在手者 solver 不产出 cardback（随 pod 隐藏，AC4-3）。
   */
  const oppCards = useMemo(() => {
    if (!overlay) return [];
    const out: { seat: number; x: number; y: number; rot: number; hw: number; hh: number }[] = [];
    for (const el of overlay.elements) {
      if (el.kind !== 'cardback' || el.seat === null) continue;
      out.push({ seat: el.seat, x: el.obb.cx, y: el.obb.cy, rot: el.obb.rot, hw: el.obb.hw, hh: el.obb.hh });
    }
    return out;
  }, [overlay]);

  /**
   * Round25 · C2（design §4.2.3，根因 §「不是所有图层都进装箱」根治）：**删除** Round23 在此另起的
   * 「Table 层放大 reveal fan（落点 = seat/pod 中心，绕过 SeatCard、不进装箱）」。摊牌 face-up 亮牌
   * 改由**统一 solver 锚点**（`.felt__cardfan`，坐标来自 `overlay` 的 cardback 元素，已进装箱、避让
   * 受保护中心带与邻座）承载：全揭示态下已弃牌者也由 solver 产出牌背槽位（见 `SeatRuntime.revealAll`）。
   * `showdownEnlarge` 仅作为**放大样式开关**（`.felt__cards--showdown` 逐档放大到花色可辨，沿 seat→pot 轴
   * 朝桌心留白长大，z=seat-pop:30 压邻座 pod、仍低于公共牌带 40），**不再另起独立图层**。
   * 联机 `MpTable` 不走本组件、`revealAll` 恒 false，行为不变（AC-4.3）。
   */
  const showdownEnlarge = stackRing && revealAll;
  /**
   * 摊牌放大牌扇的「背离底池」平移原点：底池中心（层坐标），与 overlay 求解口径一致
   * （P = { x: box.w/2, y: 中心带竖向中点 }，见 overlay useMemo）。放大时据此把牌扇沿背离 P 方向
   * 平移 (enlarged 半尺寸 − 常态 OBB 半尺寸)，令近中心带边缘守恒、放大牌不越受保护中心带。
   */
  const potCenter = geom?.band ? { x: geom.box.w / 2, y: (geom.band.top + geom.band.bottom) / 2 } : null;

  /**
   * SB/BB 盲注徽标派生（AC-L5 / AC-L7）：坐标由 solver 下发（贴 pod 角槽、避让 chip/牌背），
   * 由 felt 级 `.felt__blinds` 层渲染。庄家 D 见下方 `dealer`；英雄自身盲注仍由 SeatCard 承载。
   */
  const blindBadges = useMemo(() => {
    if (!overlay) return [];
    const out: { seat: number; x: number; y: number; kind: 'sb' | 'bb'; text: string }[] = [];
    for (const el of overlay.elements) {
      if (el.kind !== 'badge' || (el.role !== 'SB' && el.role !== 'BB') || el.seat === null) continue;
      out.push({ seat: el.seat, x: el.obb.cx, y: el.obb.cy, kind: el.role === 'SB' ? 'sb' : 'bb', text: el.role === 'SB' ? '小盲' : '大盲' });
    }
    return out;
  }, [overlay]);

  /**
   * FR-2 · AC2-6：庄家钮 D（`.felt__dealer`，桌内侧、随手轮转）。
   * 对手 D 由 solver 统一占位下发（id='dealer'，避让 pod/牌背/chip，AC-L3 D×牌背=0）；
   * 按钮落在英雄时 solver 不含英雄卫星，退回沿英雄 pod→底池射线的固定偏移（英雄区独占，无相撞源）。
   */
  const dealer = useMemo(() => {
    if (!stackRing || !ring || !geom?.band) return null;
    const btn = game.buttonSeat;
    if (btn < 0) return null;
    if (game.players[btn]?.isHuman) {
      const S = ring.me;
      const P = { x: geom.box.w / 2, y: (geom.band.top + geom.band.bottom) / 2 };
      const dx = P.x - S.x;
      const dy = P.y - S.y;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len;
      const uy = dy / len;
      /**
       * R21-M5 · BUG-02（英雄庄家钮归属 κ）：英雄 D 不进 solver，此前用固定偏移
       * `off = podEdge + 16`，满桌时英雄 pod 大（d_own≈77）+ 最近对手 pod 很近（360×10 实测 d_near=122）
       * → κ=0.631>0.6（归属歧义）。改为**归属感知落位**：先取贴靠基准 off，再用实测最近对手 pod 中心
       * （`ring.seats`，与 QA/solver 同一 pod 中心口径）反解安全 off，令 κ=d_own/d_near ≤ κ_safe(0.55) 留余量。
       * 只会**收小** off（D 更贴 pod → d_own↓、离对手更远 → d_near↑，κ 双向下降；edgeGap 更小仍 ≤12），
       * 稀疏局（d_near 大）保持原偏移不变（视觉零回归）。
       */
      const podEdge = Math.abs(ux) * (geom.me.w / 2) + Math.abs(uy) * (geom.me.h / 2);
      let off = podEdge + 16;
      const dNearAt = (o: number): number => {
        let m = Infinity;
        for (const pt of ring.seats) {
          const d = Math.hypot(S.x + ux * o - pt.x, S.y + uy * o - pt.y);
          if (d < m) m = d;
        }
        return m;
      };
      const dNear = dNearAt(off);
      if (Number.isFinite(dNear) && off > 0.55 * dNear) off = Math.max(podEdge * 0.5, 0.55 * dNear);
      const dy0 = S.y + uy * off;
      // 英雄 D 落在 pod 顶与中心带之间；短屏（360/375）hero pod 贴带时，钳住 D 不侵入公共牌带
      // （band.bottom 之下留 D 半径 9 + 间隙 4），杜绝 dealer×band（AC-L3 同源：D 不压受保护中心带）。
      const yClamped = Math.max(dy0, geom.band.bottom + 9 + 4);
      return { x: S.x + ux * off, y: yClamped, seat: btn };
    }
    const el = overlay?.elements.find((e) => e.id === 'dealer');
    return el ? { x: el.obb.cx, y: el.obb.cy, seat: btn } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackRing, ring, geom, game.buttonSeat, game.players, overlay]);

  return (
    <div
      ref={feltRef}
      className={`felt felt--${layout}${stackRing ? ' felt--stack-ring' : ''} felt--seats-${density}`}
      style={feltStyle}
      role="group"
      aria-label="牌桌"
      data-seat-plan={planTag}
    >
      {/**
       * 问题1 毛毡拆层（设计 §4.1）：视觉毛毡本体作为独立层，与 `.felt__seats` **同级不嵌套**。
       * surface 自身 `overflow:hidden` 只裁渐变/桌沿（内缩 `--felt-inset` 安全环），席位层不落在其
       * 裁剪上下文里 → pod 骑毛毡外圈 0 裁切。仅竖屏环布生效（oval 仍用 `.felt` 自带毛毡视觉）。
       */}
      {stackRing ? <div className="felt__surface" aria-hidden="true" /> : null}

      {/* 公共牌 + 底池：受保护区域，任何布局下都完整可见，不被席位或浮层覆盖 */}
      <div className="felt__center">
        <div className="board" aria-label={`公共牌，当前${STREET_CN[game.street]}`}>
          {Array.from({ length: 5 }, (_, i) => {
            const card = game.board[i];
            return card === undefined ? (
              <span key={i} className="board__slot" aria-hidden="true" />
            ) : (
              <PlayingCard key={i} card={card} size="xl" dealIndex={i >= 3 ? 0 : i} highlight={winSet.has(card)} />
            );
          })}
        </div>
        <div className="pot">
          <span className="pot__label">底池</span>
          <span className="pot__value num">{chips(game.pot)}</span>
        </div>
        {game.phase === 'handComplete' && c.handSummary?.awards.length ? (
          <ul className="pot__awards">
            {c.handSummary.awards.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="felt__seats">{others.map(renderSeat)}</div>

      {/**
       * 竖屏环布：对手手牌牌背/揭示牌**统一层**（Round25 C2 §4.2.3）。坐标一律来自 `overlay` 的 cardback
       * 元素（solver 已装箱、避让受保护中心带 8px 与邻座、含 ±6° 旋转），**不再有独立放大扇形层**。
       * 摊牌全揭示（`showdownEnlarge`）时加 `.felt__cards--showdown`：逐档放大到花色可辨、z=seat-pop:30
       * 压邻座 pod，仍低于公共牌带 40；已弃牌者由 solver 产出的槽位 face-up 亮出（去饱和 + 「已弃牌」标）。
       */}
      {oppCards.length ? (
        <div className={`felt__cards${showdownEnlarge ? ' felt__cards--showdown' : ''}`} aria-hidden="true">
          {oppCards.map((cd) => {
            const p = game.players[cd.seat];
            if (!p || !p.hole) return null;
            const rev = revealAll || revealMap.has(cd.seat);
            // 摊牌放大态取消 ±6° 旋转（面朝上、沿 seat→pot 轴朝桌心正向长大，稳定可读）；常态保留牌背旋转。
            const enlarged = showdownEnlarge && rev;
            const transform = enlarged ? 'translate(-50%, -50%)' : `translate(-50%, -50%) rotate(${cd.rot}rad)`;
            /**
             * C2 摊牌放大位移（QA-BUG-R25-A2）：放大扇比常态牌背 OBB 大，若仍以 solver 锚点为中心
             * 对称放大，则近中心带一侧会越过 board 顶沿（3 人桌椭圆顶弧 pod 尤甚）。这里把放大扇沿
             * **背离底池** 方向按逐轴半尺寸增量平移，使「近中心带边缘」与常态牌背对齐（solver 已让出 8px）。
             */
            let x = cd.x;
            let y = cd.y;
            if (enlarged && potCenter) {
              const fan = SHOWDOWN_FAN_HALF[density];
              const dx = cd.x - potCenter.x;
              const dy = cd.y - potCenter.y;
              x += Math.sign(dx) * Math.max(0, fan.hw - cd.hw);
              y += Math.sign(dy) * Math.max(0, fan.hh - cd.hh);
            }
            const folded = rev && p.folded;
            return (
              <div
                key={cd.seat}
                className={`felt__cardfan${folded ? ' felt__cardfan--folded' : ''}`}
                data-owner-seat={cd.seat}
                style={{ left: `${x}px`, top: `${y}px`, transform }}
              >
                {rev ? (
                  <>
                    <PlayingCard card={p.hole[0]} size="sm" dimmed={p.folded} highlight={!p.folded && winSet.has(p.hole[0])} dealIndex={0} />
                    <PlayingCard card={p.hole[1]} size="sm" dimmed={p.folded} highlight={!p.folded && winSet.has(p.hole[1])} dealIndex={1} />
                    {folded ? <span className="felt__cardfan-fold">已弃牌</span> : null}
                  </>
                ) : (
                  <>
                    <PlayingCard faceDown size="sm" />
                    <PlayingCard faceDown size="sm" />
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* 竖屏环布：下注 chip 独立层（席位→底池连线上，与 pod 解耦，AC3-3）。pointer-events:none、不抢层。 */}
      {chipDots.length ? (
        <div className="felt__chips" aria-hidden="true">
          {chipDots.map((d) => (
            <div key={d.seat} className="felt__chip" data-owner-seat={d.seat} style={{ left: `${d.x}px`, top: `${d.y}px` }}>
              <span className="chipstack" />
              <span className="num">{chipsShort(d.amount)}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* FR-3：操作 pill 独立层（紧邻 chip 朝底池一侧 / 无下注锚 pod 桌内侧，AC3-3）。 */}
      {actPills.length ? (
        <div className="felt__pills" aria-hidden="true">
          {actPills.map((p) => (
            <div key={p.seat} className={`felt__actpill felt__actpill--${p.cls}`} data-owner-seat={p.seat} style={{ left: `${p.x}px`, top: `${p.y}px` }}>
              {p.text}
            </div>
          ))}
        </div>
      ) : null}

      {/* 盲注 SB/BB 徽标独立层（solver 下发角槽坐标，AC-L5 / AC-L7）。--z-seat-badge。 */}
      {blindBadges.length ? (
        <div className="felt__blinds" aria-hidden="true">
          {blindBadges.map((bb) => (
            <div key={`${bb.seat}-${bb.kind}`} className={`felt__blindbadge felt__blindbadge--${bb.kind}`} data-owner-seat={bb.seat} style={{ left: `${bb.x}px`, top: `${bb.y}px` }}>
              {bb.text}
            </div>
          ))}
        </div>
      ) : null}

      {/* FR-2：庄家钮 D（桌内侧、随手轮转，AC2-6）。 */}
      {dealer ? (
        <div className="felt__dealer" aria-hidden="true" data-owner-seat={dealer.seat} style={{ left: `${dealer.x}px`, top: `${dealer.y}px` }}>
          D
        </div>
      ) : null}

      {mySeats.map(renderSeat)}
    </div>
  );
}

